import { useEffect, useState, type FormEvent } from "react";
import { api, callableErrorMessage } from "../lib/api";

type Props = {
  tenantId: string;
  initialTickets?: number;
  initialReward?: string;
  initialRewardItem?: string;
  onSaved?: () => void;
};

const PRESETS = [3, 5, 8, 10];

/**
 * Formulario: cuántas compras = 1 reward, y qué reward das.
 * Reusable en Welcome + /admin Ajustes.
 */
export function LoyaltyConfigEditor({
  tenantId,
  initialTickets = 5,
  initialReward = "Reward gratis",
  initialRewardItem = "",
  onSaved
}: Props) {
  const [tickets, setTickets] = useState(initialTickets);
  const [rewardLabel, setRewardLabel] = useState(initialReward);
  const [rewardItem, setRewardItem] = useState(initialRewardItem);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setTickets(initialTickets);
    setRewardLabel(initialReward);
    setRewardItem(initialRewardItem);
  }, [initialTickets, initialReward, initialRewardItem]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await api.updateTenantLoyaltyConfig({
        tenantId,
        config: {
          ticketsForReward: tickets,
          rewardLabel: rewardLabel.trim() || "Reward gratis",
          rewardItem: rewardItem.trim() || undefined
        }
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      onSaved?.();
    } catch (err) {
      setError(callableErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <span className="text-xs uppercase tracking-wider text-leaf-600">
          ¿Cuántas compras = 1 reward?
        </span>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setTickets(n)}
              className={`rounded-2xl border-2 px-4 py-3 text-base font-semibold transition ${
                tickets === n
                  ? "border-leaf-700 bg-leaf-700 text-white"
                  : "border-leaf-200 bg-white text-leaf-700"
              }`}
            >
              {n}
            </button>
          ))}
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              max={50}
              value={tickets}
              onChange={(e) => setTickets(parseInt(e.target.value, 10) || 1)}
              className="input w-24 text-center"
            />
          </div>
        </div>
      </div>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-leaf-700">Cómo se llama el reward (lo que ve el cliente)</span>
        <input
          value={rewardLabel}
          onChange={(e) => setRewardLabel(e.target.value)}
          maxLength={60}
          placeholder="Café gratis · Matcha gratis · Tu producto"
          className="input"
        />
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-leaf-700">Producto específico (opcional, para tu staff)</span>
        <input
          value={rewardItem}
          onChange={(e) => setRewardItem(e.target.value)}
          maxLength={80}
          placeholder="Cualquier café de la carta · Matcha mediano"
          className="input"
        />
      </label>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {saved && (
        <div className="rounded-2xl border border-leaf-200 bg-leaf-50 px-4 py-3 text-sm text-leaf-900">
          ✓ Guardado
        </div>
      )}

      <button type="submit" disabled={busy} className="btn-primary">
        {busy ? "Guardando…" : "Guardar"}
      </button>
    </form>
  );
}
